
import { Component, Mixins } from 'vue-property-decorator';
import { BAIconsMixin, VuetifyMixin, StringsMixin, RoutingMixin } from '@/mixins';
import { DataTableHeader } from 'vuetify';
import { CSVUploadMixin } from '@/mixins/CSVUploadMixin';
import { PhysicalAssessmentDataModelEx, AthleteAssessmentDataModel, AthleteAssessmentDataModelEx } from '@/models';
import { SportName } from '@/../types';
import SportSelector from '@/components/forms/SportSelector.vue';
import { athleteAssessmentDataApi } from '@/api/AthleteAssessmentDataApi';

@Component({
    components: {SportSelector}
})
export default class VerifiedAssessmentImportV5 extends Mixins(VuetifyMixin, BAIconsMixin, StringsMixin, CSVUploadMixin, RoutingMixin) {
	showUploadDialog: boolean = false;
    showDataDialog: boolean = false;
    isLoading: boolean = false;
    isLoaded: boolean = false;
    sport: SportName;

	sampleCSVData = [
		{
            assessmentDate: 'Dec 1 2025',
            firstName: 'Aiden',
            lastName: 'Anderson',
            email: 'aa@bestathletes.co',
            dateOfBirth: 'Apr 16 2008',
        },
        {
            assessmentDate: 'Dec 1 2025',
            firstName: 'Brayden',
            lastName: 'Brown',
            email: 'bb@bestathletes.co',
            dateOfBirth: 'May 3 2008',
        }
    ]

    onParseData = (csvString: string): Array<PhysicalAssessmentDataModelEx> => {
        this.isLoading = true;
        this.isLoaded = false;
        const rowJson = this.csv2json(csvString);
        const assessments = rowJson.map(row => {
            const assessment: PhysicalAssessmentDataModelEx = new PhysicalAssessmentDataModelEx();
            const keys = Object.keys(row);
            // for each column in the row (key), find the matching label in the assessment and assign it
            keys.forEach(key => {
                let keyAssigned: boolean = false;
                assessment.labels.forEach(label => {
                    if( this.NormStringIncludes(key, label) ) {
                        assessment[label] = row[key];
                        keyAssigned = true;
                    }
                })
                if( !keyAssigned ) {
                    assessment.data[key] = row[key];
                }
            })
            
            return assessment;
        });

        this.isLoaded = true;
        this.isLoading = false;
        this.UpdateTableHeaders(assessments);
        return assessments;
    }

    isSynchronizing: boolean = false;
    unconfirmedAssessments: Array<PhysicalAssessmentDataModelEx> = [];
    async onDataLoaded(data: Array<PhysicalAssessmentDataModelEx>) {
        this.unconfirmedAssessments = data;
        this.showDataDialog = true;
        this.isSynchronizing = true;
        await Promise.all(this.unconfirmedAssessments.map(async(assessment) => {
            await assessment.findAthleteProfile();
        }))
        this.isSynchronizing = false;
    }

    onCancel() {
        this.isShowAll = false;
        this.showDataDialog = false;
    }

    isUploading: boolean = false;
    numUploaded: number = 0;
    async onAcceptAssessments() {
        this.isUploading = true;
        this.numUploaded = 0;
        await Promise.all(this.unconfirmedAssessments.map(async(assessment) => {
            const athleteAssessment: AthleteAssessmentDataModelEx = new AthleteAssessmentDataModelEx().load(assessment);
            if( this.IsEmpty(athleteAssessment.sport) ) athleteAssessment.sport = this.sport;
            if( this.IsNotEmpty(assessment.athleteProfile) ) {
                if( this.IsEmpty(athleteAssessment.athleteId) ) athleteAssessment.athleteId = assessment.athleteProfile.id;
                if( this.IsEmpty(athleteAssessment.sport) ) athleteAssessment.sport = assessment.athleteProfile.Sport?.name;
                if( this.IsEmpty(athleteAssessment.teamId) ) athleteAssessment.teamId = assessment.athleteProfile.currentTeam;
                if( this.IsEmpty(athleteAssessment.organizationId) ) athleteAssessment.organizationId = assessment.athleteProfile.organizationId;
                if( this.IsEmpty(athleteAssessment.playingLevel) ) athleteAssessment.playingLevel = assessment.athleteProfile.AgeGroup;
                if( this.IsEmpty(athleteAssessment.gender) ) athleteAssessment.gender = assessment.athleteProfile.gender;
                if( this.IsEmpty(athleteAssessment.dominantFoot) ) athleteAssessment.dominantFoot = assessment.athleteProfile.dominantSide;
                if( this.IsEmpty(athleteAssessment.playingPosition) ) athleteAssessment.playingPosition = assessment.athleteProfile.Position;
                if( this.IsEmpty(athleteAssessment.dateOfBirth) ) athleteAssessment.dateOfBirth = assessment.athleteProfile.BirthDate;
                if( this.IsEmpty(athleteAssessment.mass) ) athleteAssessment.mass = assessment.athleteProfile.weight;
                if( this.IsEmpty(athleteAssessment.standingHeight) ) athleteAssessment.standingHeight = assessment.athleteProfile.height;
            }

            await athleteAssessmentDataApi.save(athleteAssessment);
            this.numUploaded++;
        }))
        this.isUploading = false;
        this.showDataDialog = false;
    }

    tableHeaders: Array<DataTableHeader<any>> = [];

    async UpdateTableHeaders(assessments: Array<PhysicalAssessmentDataModelEx>) {
        this.tableHeaders = [
            { text: 'First Name', value: 'firstName' },
            { text: 'Last Name', value: 'lastName' },
            { text: 'email', value: 'email' },
            { text: 'DoB', value: 'dateOfBirth', sortable: false },
        ];
        if( assessments.length > 0 ) {
            const measurements = Object.keys(assessments[0].data);
            for( const measurement of measurements ) {
                this.tableHeaders.push({text: measurement, value: `data.${measurement}`, sortable: false});
            }
        }
        if( this.IsLargeScreen ) {
            this.tableHeaders.push({text: '', value: 'actions', sortable: false });
            this.tableHeaders.push({text: '', value: 'data-table-expand', sortable: false});
        }

        this.sport = undefined;
        if( this.tableHeaders.find(h => this.NormStringIncludes(h.text, 'yoyo')) ) this.sport = SportName.Soccer;
        else if( this.tableHeaders.find(h => this.NormStringIncludes(h.text, 'bronco')) ) this.sport = SportName.Rugby;
        if( this.IsNotEmpty(this.sport) ) this.onChangeSport(this.sport);
    }

    isSportSelected: boolean = false;
    sportImportTooltip: string;
    onChangeSport(sport: SportName) {
        this.isSportSelected = this.IsNotEmpty(sport);
        this.sportImportTooltip = this.isSportSelected? `Import verified ${this.sport} assessments` : 'Select Sport'
    }

    isShowAll: boolean = false;
}
